type Status = 'success' | 'alert' | 'neutral';

/**
 * The function `getColorClass` determines the appropriate color classes based on
 * the input parameters.
 * @param {boolean} isEmpty - The `isEmpty` parameter is a boolean flag indicating
 * whether a certain condition is empty or not.
 * @param {boolean} isSuccess - `isSuccess` is a boolean parameter that indicates
 * whether the operation was successful or not.
 * @param {'background' | 'icon'} type - The `type` parameter in the
 * `getColorClass` function specifies whether the color class is for a background
 * or an icon. It can have two possible values: `'background'` or `'icon'`.
 * @returns The function `getColorClass` returns a string value that represents the
 * CSS classes for either background or icon styling based on the provided
 * parameters `isEmpty`, `isSuccess`, and `type`.
 */
const getColorClass = (
  isEmpty: boolean,
  isSuccess: boolean,
  type: 'background' | 'icon',
): string => {
  const colorMap: Record<Status, { background: string; icon: string }> = {
    success: {
      background: 'tw-bg-primary-light-100 tw-border-primary-light-500',
      icon: 'tw-text-primary-light-500',
    },
    alert: {
      background: 'tw-bg-alert-100 tw-border-alert-500',
      icon: 'tw-text-alert-500',
    },
    neutral: {
      background: 'tw-bg-neutral-light-200 tw-border-neutral-light-500',
      icon: 'tw-text-neutral-light-600',
    },
  };

  if (isEmpty) {
    return colorMap.neutral[type];
  }

  return isSuccess ? colorMap.success[type] : colorMap.alert[type];
};

export const colorBackground = (isEmpty: boolean, isSuccess: boolean) =>
  getColorClass(isEmpty, isSuccess, 'background');

export const iconColor = (isEmpty: boolean, isSuccess: boolean) =>
  getColorClass(isEmpty, isSuccess, 'icon');
